import { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { usersApi } from '../api';
import type { UsersListParams } from '../types';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function UsersListPage() {
  const [params, setParams] = useState<UsersListParams>({ page: 1, pageSize: 20 });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState<'name'|'email'|'role'|'isActive'|'createdAt'|'updatedAt'|'lastLoginAt'>('createdAt');
  const [dir, setDir] = useState<'asc'|'desc'>('desc');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<any | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['app-users-search', params, debouncedQuery, sort, dir],
    queryFn: () => usersApi.search({
      page: params.page || 1,
      pageSize: params.pageSize || 20,
      sort,
      dir,
      ...(debouncedQuery ? { q: debouncedQuery } : {}),
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: 'User deleted' });
      setDeletingUser(null);
      setIsDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'Failed to delete user' });
    },
  });

  const rows: any[] = (data as any)?.data || [];
  const total: number = (data as any)?.total ?? rows.length;

  const applySort = (column: typeof sort) => {
    const newDir: 'asc'|'desc' = sort === column ? (dir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSort(column);
    setDir(newDir);
  };

  // Debounce typing in search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Usuarios</h1>
            <p className="text-muted-foreground">Gestiona usuarios del sistema</p>
          </div>
          <Button asChild>
            <Link to="/users/new">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Usuario
            </Link>
          </Button>
        </div>

        <Card className="p-6 mb-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button onClick={() => { setParams({ ...params, page: 1 }); setDebouncedQuery(searchQuery); }}>
              <Search className="mr-2 h-4 w-4" />
              Buscar
            </Button>
          </div>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead onClick={()=>applySort('name')} className="cursor-pointer select-none">
                  Nombre {sort==='name' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                </TableHead>
                <TableHead onClick={()=>applySort('email')} className="cursor-pointer select-none">
                  Email {sort==='email' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                </TableHead>
                <TableHead onClick={()=>applySort('role')} className="cursor-pointer select-none">
                  Rol {sort==='role' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                </TableHead>
                <TableHead onClick={()=>applySort('isActive')} className="cursor-pointer select-none">
                  Activo {sort==='isActive' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                </TableHead>
                <TableHead onClick={()=>applySort('lastLoginAt')} className="cursor-pointer select-none">
                  Último login {sort==='lastLoginAt' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                </TableHead>
                <TableHead onClick={()=>applySort('createdAt')} className="cursor-pointer select-none">
                  Creado {sort==='createdAt' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
                </TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No se encontraron usuarios
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((u: any) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.id}</TableCell>
                    <TableCell className="font-medium">{u.full_name || u.fullName || '-'}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.roleName || u.role_name || '-'}</TableCell>
                    <TableCell>{(u.isActive ?? u.is_active) ? 'Sí' : 'No'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="space-x-1">
                      <Button variant="ghost" size="icon" asChild aria-label="Editar" title="Editar">
                        <Link to={`/users/${u.id}/edit`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setDeletingUser(u);
                          setIsDeleteDialogOpen(true);
                        }}
                        aria-label="Eliminar"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="p-4 border-t flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Mostrando {rows.length} de {total} resultados</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={(params.page || 1) === 1}
                onClick={() => setParams({ ...params, page: (params.page || 1) - 1 })}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(params.page || 1) * (params.pageSize || 20) >= total}
                onClick={() => setParams({ ...params, page: (params.page || 1) + 1 })}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </Card>

        {/* Delete confirmation */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Se eliminará permanentemente el usuario{' '}
                <strong>{deletingUser?.full_name || deletingUser?.fullName}</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deletingUser && deleteMutation.mutate(deletingUser.id)}
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
